/**
 * src/pages/Clients.jsx — searchable client list.
 *
 * Search is debounced and sent to the server rather than filtering in the
 * browser, because a long-running studio's client list is not something you
 * want to ship to the browser in full on every page load.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { api } from '../lib/api.js';
import { useApi } from '../lib/useApi.js';
import { formatDate } from '../lib/format.js';
import { Empty, ErrorBanner, Loader, Modal, PageHeader } from '../components/ui.jsx';

export default function Clients() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [adding, setAdding] = useState(false);

  // 250ms is long enough to stop a request per keystroke and short enough that
  // the list still feels live.
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const query = `/api/clients${debounced ? `?search=${encodeURIComponent(debounced)}` : ''}`;
  const { data, error, loading, reload } = useApi(query, [debounced]);
  const clients = data?.clients ?? [];

  return (
    <>
      <PageHeader
        title="Clients"
        subtitle={loading ? 'Loading…' : `${clients.length} ${clients.length === 1 ? 'client' : 'clients'}`}
        actions={
          <>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or email"
              aria-label="Search clients"
              style={{ width: 240 }}
            />
            <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>
              Add client
            </button>
          </>
        }
      />

      <div className="card">
        <Loader loading={loading} error={error}>
          {clients.length === 0 ? (
            <Empty>
              {debounced ? 'No clients match that search.' : 'No clients yet — add your first one.'}
            </Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Projects</th>
                    <th>Last in</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c) => (
                    <tr
                      key={c.id}
                      className="clickable"
                      onClick={() => navigate(`/clients/${c.id}`)}
                    >
                      <td><Link to={`/clients/${c.id}`}>{c.name}</Link></td>
                      <td className="muted">{c.email}</td>
                      <td className="muted">{c.phone ?? '—'}</td>
                      <td className="mono">{c.projectCount}</td>
                      <td className="muted mono">{formatDate(c.lastAppointmentAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Loader>
      </div>

      {adding ? (
        <AddClient
          onClose={() => setAdding(false)}
          onCreated={() => {
            reload();
            setAdding(false);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * New-client form.
 *
 * @param {{ onClose: () => void, onCreated: () => void }} props
 * @returns {JSX.Element}
 */
function AddClient({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/clients', {
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        notes: form.notes || undefined,
      });
      onCreated();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal title="Add client" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <ErrorBanner>{error}</ErrorBanner>
        <div>
          <label htmlFor="cname">Name</label>
          <input id="cname" value={form.name} onChange={set('name')} required />
        </div>
        <div>
          <label htmlFor="cemail">Email</label>
          <input id="cemail" type="email" value={form.email} onChange={set('email')} required />
        </div>
        <div>
          <label htmlFor="cphone">Phone</label>
          <input id="cphone" value={form.phone} onChange={set('phone')} />
        </div>
        <div>
          <label htmlFor="cnotes">Notes</label>
          <textarea
            id="cnotes"
            value={form.notes}
            onChange={set('notes')}
            placeholder="Allergies, skin type, anything worth remembering."
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Saving…' : 'Add client'}
        </button>
      </form>
    </Modal>
  );
}
